import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../utils/api";
import { API_ENDPOINTS } from "../config/constants";
import { getReadableApiError } from "../utils/readableApiResponse";
import { getFallbackEmailValidationMessage } from "../utils/textValidation";

const initialMetrics = {
    pending: 0,
    processing: 0,
    sent: 0,
    skipped: 0,
    failed: 0,
};

const initialState = {
    campaigns: [], // stores all campaigns
    groupedCampaigns: {}, // stores all campaigns, but grouped under all searches

    selectedCampaign: null, // stores single campaign, when user clicks "view campaign"
    campaignsBySearch: [], // stores all cmpaigns brlonging to one search

    blueprintPreview: null,
    previewStatus: "idle",
    previewError: null,

    approveStatus: "idle",
    approveError: null,
    lastApproveResponse: null,

    capStatus: null,
    capStatusStatus: "idle",
    capStatusError: null,

    statusGuard: null,
    statusGuardStatus: "idle",
    statusGuardError: null,

    dashboardStatus: "idle", // used when fetching all campaings (ideal, loading, succeeded, failed)
    dashboardError: null,
    detailsStatus: "idle", // used when fetching single campaign detailed
    detailsError: null,

    updateCampaignContextStatus: "idle",
    updateCampaignContextError: null,
    updateCampaignContextMessage: null,

    updateCampaignStatusStatus: "idle",
    updateCampaignStatusError: null,
    updateCampaignStatusMessage: null,
};


const getSequenceId = (campaign) => campaign?.sequence_id || campaign?.id || "";

const getSearchId = (campaign) =>
    campaign?.search_id || campaign?.searchId || "unknown_search";

const normalizeCampaign = (campaign = {}) => ({
    ...campaign,
    sequence_id: getSequenceId(campaign),
    search_id: getSearchId(campaign),
    search_query: campaign.search_query || "Unknown search",
    metrics: {
        ...initialMetrics,
        ...(campaign.metrics || {}),
    },
});

const normalizeCampaignList = (campaigns) => {
    if (!Array.isArray(campaigns)) return [];

    const uniqueCampaigns = new Map();

    campaigns.forEach((campaign) => {
        const normalizedCampaign = normalizeCampaign(campaign);
        const sequenceId = normalizedCampaign.sequence_id;

        if (!sequenceId) return;

        uniqueCampaigns.set(sequenceId, normalizedCampaign);
    });

    return Array.from(uniqueCampaigns.values());
};

const normalizeBlueprintPreview = (preview) => {
    if (!preview) return null;

    return {
        ...preview,
        designed_steps: Array.isArray(preview.designed_steps)
            ? preview.designed_steps
            : [],
    };
};

const getTrimmedString = (value) =>
    typeof value === "string" ? value.trim() : "";

const isEmailChannel = (channelType) =>
    getTrimmedString(channelType).toLowerCase() === "email";

const getProductId = (productId, state) =>
    getTrimmedString(productId) ||
    getTrimmedString(state?.products?.selectedProduct?.id);

const getRequiredBlueprintFields = (blueprintData = {}) => ({
    product_id: getTrimmedString(blueprintData.product_id),
    campaign_objective: getTrimmedString(blueprintData.campaign_objective),
    target_audience: getTrimmedString(blueprintData.target_audience),
});

const getCampaignContextFields = (blueprintData = {}) => {
    const campaignContext = blueprintData.campaign_context || {};
    const campaignObjective = getTrimmedString(
        blueprintData.campaign_objective,
    );

    return {
        campaign_objective: campaignObjective,
        product_value_proposition: getTrimmedString(
            campaignContext.product_value_proposition,
        ),
        tone_setting: getTrimmedString(campaignContext.tone_setting),
    };
};

const normalizeApprovedSteps = (steps) => {
    if (!Array.isArray(steps)) return [];

    return steps
        .map((step) => ({
            step_number: step?.step_number,
            delay_days_from_previous: step?.delay_days_from_previous ?? 0,
            channel_type: getTrimmedString(step?.channel_type),
            step_objective: getTrimmedString(step?.step_objective),
            subject: getTrimmedString(step?.subject),
            html_body: getTrimmedString(step?.html_body),
        }))
        .filter(
            (step) =>
                step.step_number !== undefined &&
                step.step_number !== null &&
                step.channel_type &&
                step.step_objective,
        );
};

const groupCampaignsBySearch = (campaigns) => {
    return campaigns.reduce((groups, campaign) => {
        const searchId = getSearchId(campaign);

        if (!groups[searchId]) {
            groups[searchId] = {
                search_query: campaign.search_query || "Unknown search",
                campaigns: [],
            };
        }

        groups[searchId].campaigns.push(campaign);

        return groups;
    }, {});
};

const getSequenceEndpoint = (sequenceId) => {
    return `${API_ENDPOINTS.OUTREACH_SEQUENCES}/${encodeURIComponent(sequenceId)}`;
};

const getOutreachApiError = (error, fallbackMessage) => {
    const detail = error.response?.data?.detail;

    if (typeof detail === "string") {
        return detail;
    }

    if (detail?.message) {
        return detail.message;
    }

    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) => item?.message || item?.msg || item?.type)
            .filter(Boolean);

        if (messages.length > 0) {
            return messages.join(" ");
        }
    }

    return error.response?.data?.message || error.message || fallbackMessage;
};

const getResponseMessage = (responseData) => {
    if (typeof responseData === "string") {
        return responseData;
    }

    return responseData?.message || null;
};

const getApproveBlueprintError = (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const traceId = detail?.trace_id || error.response?.data?.trace_id;

    if (
        status === 422 &&
        detail?.error_code === "BLUEPRINT_INCOMPLETE"
    ) {
        return {
            type: "blueprint_incomplete",
            error_code: detail.error_code,
            step_number: detail.step_number,
            trace_id: detail.trace_id,
            block_reason: detail.block_reason,
            remediation: detail.remediation,
        };
    }

    if (status === 402 && detail?.error_code === "SUBSCRIPTION_INACTIVE") {
        return {
            message:
                "Your subscription is inactive. Reactivate it to launch campaigns.",
            error_code: detail.error_code,
            trace_id: traceId,
        };
    }

    if (detail?.error_code === "ACTIVE_SEQUENCE_LIMIT_REACHED") {
        return {
            message:
                "Active campaign limit reached. Pause or complete an existing campaign, or upgrade your plan.",
            error_code: detail.error_code,
            trace_id: traceId,
        };
    }

    if (status === 409 || detail?.error_code === "LOCK_CONFLICT") {
        return {
            message:
                "Another campaign is being launched for this workspace. Please wait a few seconds and try again.",
            error_code: detail?.error_code || "LOCK_CONFLICT",
            trace_id: traceId,
        };
    }

    if (status === 502 || status === 503) {
        return {
            message:
                "The campaign service is temporarily unavailable. Please try again shortly.",
            trace_id: traceId,
        };
    }

    if (status === 500) {
        return {
            message:
                "Something went wrong while launching the campaign. Please try again.",
            trace_id: traceId,
        };
    }

    return {
        message:
            "Something went wrong while launching the campaign. Please try again.",
        trace_id: traceId,
    };
};

export const previewCampaignBlueprint = createAsyncThunk(
    "outreach/previewCampaignBlueprint",
    async (blueprintData, thunkAPI) => {
        const requiredFields = getRequiredBlueprintFields(blueprintData);
        const campaignContext = getCampaignContextFields({
            ...blueprintData,
            campaign_objective: requiredFields.campaign_objective,
        });
        const requestBody = {
            ...requiredFields,
            product_id: getProductId(
                blueprintData?.product_id,
                thunkAPI.getState(),
            ),
            campaign_context: campaignContext,
        };

        if (!requestBody.product_id) {
            return thunkAPI.rejectWithValue(
                "Create or select a product before previewing campaign blueprint.",
            );
        }

        if (
            !requestBody.campaign_objective ||
            !requestBody.target_audience ||
            !campaignContext.product_value_proposition ||
            !campaignContext.tone_setting
        ) {
            return thunkAPI.rejectWithValue(
                "Campaign objective, target audience, product value proposition, and tone setting are required.",
            );
        }

        try {
            const response = await api.post(
                API_ENDPOINTS.OUTREACH_BLUEPRINT_PREVIEW,
                requestBody,
            );
            return normalizeBlueprintPreview(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to preview campaign blueprint.",
                ),
            );
        }
    },
);

export const approveCampaignBlueprint = createAsyncThunk(
    "outreach/approveCampaignBlueprint",
    async (approvalData = {}, thunkAPI) => {
        const baseFields = {
            ...getRequiredBlueprintFields(approvalData),
            product_id: getProductId(
                approvalData.product_id,
                thunkAPI.getState(),
            ),
        };
        const campaignContext = getCampaignContextFields({
            ...approvalData,
            campaign_objective: baseFields.campaign_objective,
        });
        const requestBody = {
            organization_id: getTrimmedString(approvalData.organization_id),
            ...baseFields,
            search_id: getTrimmedString(approvalData.search_id),
            sequence_name: getTrimmedString(approvalData.sequence_name),
            campaign_context: campaignContext,
            lead_ids: Array.isArray(approvalData.lead_ids)
                ? approvalData.lead_ids.filter(Boolean)
                : [],
            approved_steps: normalizeApprovedSteps(
                approvalData.approved_steps,
            ),
        };

        if (
            !requestBody.organization_id ||
            !requestBody.search_id ||
            !requestBody.sequence_name ||
            !requestBody.campaign_objective ||
            !requestBody.target_audience ||
            !campaignContext.product_value_proposition ||
            !campaignContext.tone_setting
        ) {
            return thunkAPI.rejectWithValue(
                "Organization, search, sequence name, objective, target audience, product value proposition, and tone setting are required.",
            );
        }

        if (!requestBody.product_id) {
            return thunkAPI.rejectWithValue(
                "Create or select a product before approving campaign blueprint.",
            );
        }

        if (requestBody.lead_ids.length === 0) {
            return thunkAPI.rejectWithValue(
                "At least one lead is required to start a campaign.",
            );
        }

        if (requestBody.approved_steps.length === 0) {
            return thunkAPI.rejectWithValue(
                "At least one approved step is required to start a campaign.",
            );
        }

        const invalidFallbackEmailMessage = requestBody.approved_steps.reduce(
            (message, step) => {
                if (message || !isEmailChannel(step.channel_type)) {
                    return message;
                }

                return getFallbackEmailValidationMessage({
                    subject: step.subject,
                    html_body: step.html_body,
                });
            },
            "",
        );

        if (invalidFallbackEmailMessage) {
            return thunkAPI.rejectWithValue(invalidFallbackEmailMessage);
        }

        try {
            const response = await api.post(
                API_ENDPOINTS.OUTREACH_BLUEPRINT_APPROVE,
                requestBody,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(getApproveBlueprintError(error));
        }
    },
);

export const fetchOutreachCapStatus = createAsyncThunk(
    "outreach/fetchOutreachCapStatus",
    async (_, thunkAPI) => {
        try {
            const response = await api.get(API_ENDPOINTS.OUTREACH_CAP_STATUS);

            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to fetch campaign cap status.",
                ),
            );
        }
    },
);

export const checkCampaignStatusGuard = createAsyncThunk(
    "outreach/checkCampaignStatusGuard",
    async ({ searchId, productId } = {}, thunkAPI) => {
        const normalizedSearchId = getTrimmedString(searchId);
        const normalizedProductId = getTrimmedString(productId);

        if (!normalizedSearchId || !normalizedProductId) {
            return thunkAPI.rejectWithValue(
                "Search ID and Product ID are required.",
            );
        }

        try {
            const response = await api.get(API_ENDPOINTS.OUTREACH_STATUS_GUARD, {
                params: {
                    search_id: normalizedSearchId,
                    product_id: normalizedProductId,
                },
            });

            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getOutreachApiError(
                    error,
                    "Failed to check campaign status.",
                ),
            );
        }
    },
);

// fetch all campaings user has created till now
export const fetchOutreachCampaigns = createAsyncThunk(
    "outreach/fetchOutreachCampaigns",
    async (_, thunkAPI) => {
        try {
            const response = await api.get(
                API_ENDPOINTS.OUTREACH_SEQUENCE_DASHBOARD,
            );
            return normalizeCampaignList(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch campaigns."),
            );
        }
    },
);

// fetches campaign info
export const fetchCampaignDetails = createAsyncThunk(
    "outreach/fetchCampaignDetails",
    async (sequenceId, thunkAPI) => {
        if (!sequenceId) {
            return thunkAPI.rejectWithValue("Invalid campaign id.");
        }

        try {
            const response = await api.get(getSequenceEndpoint(sequenceId));
            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(error, "Failed to fetch campaign details."),
            );
        }
    },
);

// fetches all campaings belonging to one search
export const fetchCampaignsBySearch = createAsyncThunk(
    "outreach/fetchCampaignsBySearch",
    async (searchId, thunkAPI) => {
        if (!searchId) {
            return thunkAPI.rejectWithValue("Invalid search id.");
        }
        try {
            const response = await api.get(
                `${API_ENDPOINTS.OUTREACH_SEQUENCES}/by-search/${encodeURIComponent(searchId)}`,
            );
            return normalizeCampaignList(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getReadableApiError(
                    error,
                    "Failed to fetch campaigns for search.",
                ),
            );
        }
    },
);

export const updateCampaignContext = createAsyncThunk(
    "outreach/updateCampaignContext",
    async (
        {
            sequenceId,
            campaign_objective,
            product_value_proposition,
            tone_setting,
        } = {},
        thunkAPI,
    ) => {
        const normalizedSequenceId = getTrimmedString(sequenceId);
        const requestBody = {
            campaign_objective: getTrimmedString(campaign_objective),
            product_value_proposition: getTrimmedString(
                product_value_proposition,
            ),
        };
        const toneSetting = getTrimmedString(tone_setting);

        if (!normalizedSequenceId) {
            return thunkAPI.rejectWithValue("Invalid campaign id.");
        }

        if (
            !requestBody.campaign_objective ||
            !requestBody.product_value_proposition
        ) {
            return thunkAPI.rejectWithValue(
                "Campaign objective and product value proposition are required.",
            );
        }

        if (toneSetting) {
            requestBody.tone_setting = toneSetting;
        }

        try {
            const response = await api.patch(
                API_ENDPOINTS.UPDATE_CAMPAIGN_CONTEXT(normalizedSequenceId),
                requestBody,
            );

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getOutreachApiError(
                    error,
                    "Failed to update campaign context.",
                ),
            );
        }
    },
);

export const updateCampaignStatus = createAsyncThunk(
    "outreach/updateCampaignStatus",
    async ({ sequenceId, status } = {}, thunkAPI) => {
        const normalizedSequenceId = getTrimmedString(sequenceId);
        const normalizedStatus = getTrimmedString(status).toLowerCase();

        if (!normalizedSequenceId) {
            return thunkAPI.rejectWithValue("Invalid campaign id.");
        }

        if (!["paused", "active"].includes(normalizedStatus)) {
            return thunkAPI.rejectWithValue(
                "Campaign status must be paused or active.",
            );
        }

        try {
            const response = await api.patch(
                API_ENDPOINTS.UPDATE_CAMPAIGN_STATUS(normalizedSequenceId),
                { status: normalizedStatus },
            );

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getOutreachApiError(
                    error,
                    "Failed to update campaign status.",
                ),
            );
        }
    },
);

const outreachSlice = createSlice({
    name: "outreach",
    initialState,
    reducers: {
        setSelectedCampaign: (state, action) => {
            state.selectedCampaign = action.payload;
        },
        clearSelectedCampaign: (state) => {
            state.selectedCampaign = null;
            state.detailsStatus = "idle";
            state.detailsError = null;
        },
        clearBlueprintPreview: (state) => {
            state.blueprintPreview = null;
            state.previewStatus = "idle";
            state.previewError = null;
        },
        clearApproveState: (state) => {
            state.approveStatus = "idle";
            state.approveError = null;
            state.lastApproveResponse = null;
        },
        clearStatusGuardState: (state) => {
            state.statusGuard = null;
            state.statusGuardStatus = "idle";
            state.statusGuardError = null;
        },
        clearUpdateCampaignContextState: (state) => {
            state.updateCampaignContextStatus = "idle";
            state.updateCampaignContextError = null;
            state.updateCampaignContextMessage = null;
        },
        clearUpdateCampaignStatusState: (state) => {
            state.updateCampaignStatusStatus = "idle";
            state.updateCampaignStatusError = null;
            state.updateCampaignStatusMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(previewCampaignBlueprint.pending, (state) => {
                state.previewStatus = "loading";
                state.previewError = null;
                state.blueprintPreview = null;
            })
            .addCase(previewCampaignBlueprint.fulfilled, (state, action) => {
                state.previewStatus = "succeeded";
                state.previewError = null;
                state.blueprintPreview = action.payload;
            })
            .addCase(previewCampaignBlueprint.rejected, (state, action) => {
                state.previewStatus = "failed";
                state.previewError = action.payload;
            })
            .addCase(approveCampaignBlueprint.pending, (state) => {
                state.approveStatus = "loading";
                state.approveError = null;
                state.lastApproveResponse = null;
            })
            .addCase(approveCampaignBlueprint.fulfilled, (state, action) => {
                state.approveStatus = "succeeded";
                state.approveError = null;
                state.lastApproveResponse = action.payload;
            })
            .addCase(approveCampaignBlueprint.rejected, (state, action) => {
                state.approveStatus = "failed";
                state.approveError = action.payload;
            })
            .addCase(fetchOutreachCapStatus.pending, (state) => {
                state.capStatusStatus = "loading";
                state.capStatusError = null;
            })
            .addCase(fetchOutreachCapStatus.fulfilled, (state, action) => {
                state.capStatusStatus = "succeeded";
                state.capStatusError = null;
                state.capStatus = action.payload;
            })
            .addCase(fetchOutreachCapStatus.rejected, (state, action) => {
                state.capStatusStatus = "failed";
                state.capStatusError = action.payload;
            })
            .addCase(checkCampaignStatusGuard.pending, (state) => {
                state.statusGuardStatus = "loading";
                state.statusGuardError = null;
                state.statusGuard = null;
            })
            .addCase(checkCampaignStatusGuard.fulfilled, (state, action) => {
                state.statusGuardStatus = "succeeded";
                state.statusGuard = action.payload;
                state.statusGuardError = null;
            })
            .addCase(checkCampaignStatusGuard.rejected, (state, action) => {
                state.statusGuardStatus = "failed";
                state.statusGuardError = action.payload;
            })
            .addCase(fetchOutreachCampaigns.pending, (state) => {
                state.dashboardStatus = "loading";
                state.dashboardError = null;
            })
            .addCase(fetchOutreachCampaigns.fulfilled, (state, action) => {
                state.dashboardStatus = "succeeded";
                state.dashboardError = null;
                state.campaigns = action.payload;
                state.groupedCampaigns = groupCampaignsBySearch(action.payload);
            })
            .addCase(fetchOutreachCampaigns.rejected, (state, action) => {
                state.dashboardStatus = "failed";
                state.dashboardError = action.payload;
            })
            .addCase(fetchCampaignDetails.pending, (state) => {
                state.detailsStatus = "loading";
                state.detailsError = null;
            })
            .addCase(fetchCampaignDetails.fulfilled, (state, action) => {
                state.detailsStatus = "succeeded";
                state.detailsError = null;
                state.selectedCampaign = action.payload;
            })
            .addCase(fetchCampaignDetails.rejected, (state, action) => {
                state.detailsStatus = "failed";
                state.detailsError = action.payload;
            })
            .addCase(fetchCampaignsBySearch.pending, (state) => {
                state.dashboardStatus = "loading";
                state.dashboardError = null;
            })
            .addCase(fetchCampaignsBySearch.fulfilled, (state, action) => {
                state.dashboardStatus = "succeeded";
                state.dashboardError = null;
                state.campaignsBySearch = action.payload;
            })
            .addCase(fetchCampaignsBySearch.rejected, (state, action) => {
                state.dashboardStatus = "failed";
                state.dashboardError = action.payload;
            })
            .addCase(updateCampaignContext.pending, (state) => {
                state.updateCampaignContextStatus = "loading";
                state.updateCampaignContextError = null;
                state.updateCampaignContextMessage = null;
            })
            .addCase(updateCampaignContext.fulfilled, (state, action) => {
                state.updateCampaignContextStatus = "succeeded";
                state.updateCampaignContextError = null;
                state.updateCampaignContextMessage = getResponseMessage(
                    action.payload,
                );
            })
            .addCase(updateCampaignContext.rejected, (state, action) => {
                state.updateCampaignContextStatus = "failed";
                state.updateCampaignContextError = action.payload;
                state.updateCampaignContextMessage = null;
            })
            .addCase(updateCampaignStatus.pending, (state) => {
                state.updateCampaignStatusStatus = "loading";
                state.updateCampaignStatusError = null;
                state.updateCampaignStatusMessage = null;
            })
            .addCase(updateCampaignStatus.fulfilled, (state, action) => {
                state.updateCampaignStatusStatus = "succeeded";
                state.updateCampaignStatusError = null;
                state.updateCampaignStatusMessage = getResponseMessage(
                    action.payload,
                );
            })
            .addCase(updateCampaignStatus.rejected, (state, action) => {
                state.updateCampaignStatusStatus = "failed";
                state.updateCampaignStatusError = action.payload;
                state.updateCampaignStatusMessage = null;
            });
    },
});

export const {
    setSelectedCampaign,
    clearSelectedCampaign,
    clearBlueprintPreview,
    clearApproveState,
    clearStatusGuardState,
    clearUpdateCampaignContextState,
    clearUpdateCampaignStatusState,
} = outreachSlice.actions;

export default outreachSlice.reducer;
